import { Component, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import swal from 'sweetalert2/dist/sweetalert2.js';
import { BaseComponent } from '../base/base.component';
import { OrganizationDetail } from '../data/organization-detail';
import { AddOrEditOrgModalComponent } from '../add-or-edit-org-modal/add-or-edit-org-modal.component';
import { Class } from '../data/class';
import { OrganizationLicenseSummaryComponent } from '../organization-license-summary/organization-license-summary.component';
import { PurchasedLicensesInfo } from '../data/purchased-licenses-info';

enum Page
{
    SubOrgs,
    Admins,
    Classes,
    Students,
    Data,
    Licenses
}

@Component({
    selector: 'app-organization-detail',
    templateUrl: './organization-detail.component.html',
    styleUrls: ['./organization-detail.component.scss']
})
export class OrganizationDetailComponent extends BaseComponent implements OnInit
{
    Page = Page;

    addItems = [
        {
            key: Page.SubOrgs,
            description: 'Suborganization',
        },
        {
            key: Page.Classes,
            description: 'Class',
        },
        {
            key: Page.Admins,
            description: 'Administrator',
        },
        {
            key: Page.Data,
            description: 'Data',
        }
    ];

    accountID: number;
    subOrgsFullList: OrganizationDetail[];
    classesFullList: Class[];
    subOrgs: OrganizationDetail[];
    classes: Class[];
    newParentOrganization: any;
    organization: OrganizationDetail;
    organizationId: number;
    searching = false;
    terms: string = '';
    showingPage: Page;
    @ViewChild('orgAdminsListComponent', { static: false })
    orgAdminsListComponent: any;
    @ViewChild('editOrgModal', { static: false })
    editOrgModal: AddOrEditOrgModalComponent;
    searchValue: string = "";
    @ViewChild('settingsModal', { static: false }) settingsModal: OrganizationLicenseSummaryComponent;
    @ViewChild('orgLicenseDetails', { static: false }) orgLicenseDetails: any;

    // License and sales rep data - now handled by org-license-details component

    hasSubOrgs(): boolean { return Array.isArray(this.subOrgsFullList) && this.subOrgsFullList.length > 0; }
    hasClasses(): boolean { return Array.isArray(this.classesFullList) && this.classesFullList.length > 0; }
    hasAdmins(): boolean { return Array.isArray(this.organization.Administrators) && this.organization.Administrators.length > 0; }
    hasLicenses(): boolean { 
        // For internal users, assume licenses might exist (will be handled by org-license-details)
        return this.isInternal();
    }
    isSchool(): boolean { return !this.hasSubOrgs() && this.hasClasses(); }


    formatter = (result: OrganizationDetail) => result.Description;
    resultFormatter = (result: OrganizationDetail) =>
    {
        return result.Description + (result.ExternalID ? ` (${result.ExternalID})` : '');
    }


    ngOnInit()
    {
        this.accountID = this.authService.session.ID;
        this.route.params.subscribe(params =>
        {
            this.searchValue = null;
            this.deleteObj = null;
            if (params.organizationId === 'create-root' || params.organizationId === 'create-sub')
            {
                this.showingPage = Page.SubOrgs;

                this.organization = {
                    ID: 0,
                    Description: '',
                    LoginCode: '',
                    TypeCode: '',
                    TypeDescription: '',
                    Administrators: []
                };

                this.route.queryParams.subscribe(queryParams =>
                {
                    this.organization.ParentOrganizationDescription = queryParams.parentDescription;
                    this.organization.ParentOrganizationID = queryParams.parentId;
                    this.organization.TypeCode = 'ADM';
                    this.organization.TypeDescription = 'Administrator';
                    if (params.organizationId === 'create-sub')
                    {
                        this.setNewParentOrg();
                    }
                });
                this.startEdit(this.organization);
            } else
            {
                this.subOrgs = undefined;
                this.classes = undefined;
                this.cancelEdit(null);
                this.organizationId = +params.organizationId;
                this.getOrganizationDetail();
                this.route.queryParams.subscribe(queryParams =>
                {
                    this.showingPage = null;
                    if (queryParams.t)
                    {
                        this.showingPage = (<any>Page)[queryParams.t];
                        // Redirect non-internal users away from licenses tab
                        if (this.showingPage === Page.Licenses && !this.isInternal())
                        {
                            this.showingPage = Page.SubOrgs;
                            this.router.navigate([`/organizations/${this.organizationId}`], { queryParams: { t: Page[Page.SubOrgs] } });
                        }
                    }
                    else
                    {
                        this.showingPage = Page.SubOrgs;
                    }
                });
            }
        });
    }

    addItem(addItem: any)
    {
        if (addItem === Page.Admins)
        {
            this.switchTab(Page.Admins);
            setTimeout(() => { this.orgAdminsListComponent.focusForAdd(); }, 10);
        } else if (addItem === Page.Classes)
        {
            this.router.navigate(['/classes/new'], {
                queryParams: {
                    parentDescription: this.organization.Description,
                    parentId: this.organizationId,
                }
            });
        } else
        {
            this.router.navigate(['/organizations/create-sub'], {
                queryParams: {
                    parentDescription: this.organization.Description,
                    parentId: this.organizationId,
                    typeCode: addItem.key,
                    typeDescription: addItem.description,
                }
            });
        }
    }

    deleteOrganization(): void
    {
        this.dataService.promptDelete(`This will permanently delete the organization: ${this.organization.Description}.`)
            .then(() =>
            {
                this.deleteObj = this.organization;
                this.dataService.deleteOrganization(this.organization.ID).subscribe(() =>
                {
                    this.organizationService.deleteOrg(this.organization);
                    this.router.navigate([`/organizations/${this.organization.ParentOrganizationID || ''}`])
                });
            });
    }

    editOrg()
    {
        this.editOrgModal.openForEdit(this.organization);
    }

    organizationSettings(): void
    {
        // License and sales rep data are now handled by org-license-details component
        // This method may need to be updated to work with the new structure
        console.log('Organization settings - license data now handled by org-license-details');
    }

    getOrganizationDetail(): void
    {
        this.organization = null;
        // Reset only the data that should change per organization
        
        this.dataService.getOrganizationDetail(this.organizationId)
            .subscribe(o =>
            {
                this.organization = o;
                this.setNewParentOrg();
                this.classesFullList = o.Classes;
                this.classes = this.classesFullList;
                this.getSubOrgs();
                // License and sales rep data are now handled by org-license-details component
            });
    }

    filterOrgs()
    {
        this.searching = true;
        this.subOrgs = this.subOrgsFullList.filter(o => o.Description.toLowerCase().includes(this.terms));
        this.classes = this.classesFullList.filter(c => c.Description.toLowerCase().includes(this.terms));
    }

    onSearchInput(val: string)
    {
        this.searchValue = val;
    }

    performSearch(term: string): Observable<any>
    {
        return this.dataService.getSubOrganizations(this.organizationId, term);
    }

    getSubOrgs(): void
    {
        this.dataService.getSubOrganizations(this.organizationId)
            .subscribe(organizations =>
            {
                this.subOrgsFullList = organizations.filter(o => o.TypeCode !== 'CS').sort((a, b) => a.Description.localeCompare(b.Description));
                this.subOrgs = this.subOrgsFullList;
                if (!this.showingPage || (this.showingPage === (Page.SubOrgs as Page) && !this.hasSubOrgs()))
                {
                    if (this.isSchool()) this.showingPage = Page.Classes;
                    else this.showingPage = Page.SubOrgs;
                }
            });
    }


    parentOrganizationSelected(selectedOrg: OrganizationDetail)
    {
        this.organization.ParentOrganizationID = selectedOrg.ID;
        this.organization.ParentOrganizationDescription = selectedOrg.Description;
        this.newParentOrganization = '';
    }

    saveOrganization()
    {
        if (!this.organization.Description)
        {
            swal.fire('Oops!', 'Organization name is required.', 'error');
            return;
        }
        this.startSaveObj(event);

        if (this.organization.ID)
        {
            this.updateOrganization();
        }
        else
        {
            this.dataService.createOrganization(this.organization).subscribe(organization =>
            {
                if (organization)
                {
                    this.organizationService.addOrg(organization);
                    this.organizationId = organization.ID;
                    this.handleSaveSuccess();
                    this.router.navigate([`/organizations/${this.organizationId}`]);
                }
                else
                {
                    this.handleSaveError();
                }
            });
        }
    }

    isSuper()
    {
        return this.authService.isSuper();
    }

    isInternal()
    {
        return this.authService.isInternal();
    }

    setNewParentOrg()
    {
        this.newParentOrganization = { Description: this.organization.ParentOrganizationDescription };
    }

    switchTab(page: Page)
    {
        this.router.navigate([`/organizations/${this.organization.ID}`], { queryParams: { t: Page[page] } });
    }

    updateOrganization(): void
    {
        if (this.editObj && this.orgAdminsListComponent && this.orgAdminsListComponent.hasUnsavedChanges())
        {
            swal.fire({
                title: 'Are you sure?',
                text: 'You have an unsaved added administrator. Would you like to add them?',
                type: 'warning',
                showConfirmButton: true,
                showCancelButton: true,
                reverseButtons: true,
            }).then(res =>
            {
                if (res.value)
                {
                    if (this.orgAdminsListComponent.addAdmin())
                    {
                        this.updateOrganizationInternal();
                    }
                }
            })
        } else
        {
            this.updateOrganizationInternal();
        }
    }

    updateOrganizationInternal()
    {
        this.startSaveObj(event);
        this.dataService.updateOrganization(this.organization).subscribe(od =>
        {
            if (od)
            {
                this.handleSaveSuccess();
            }
            else
            {
                this.handleSaveError();
            }
        });
    }

    showReportButton(): boolean
    {
        if (this.organization.Permissions === undefined || this.organization.Permissions === null) return false;
        return this.organization.Permissions.some(p => p.PermissionCode === 'ReadOrganizations');
    }

    isSeanUser(): boolean
    {
        return this.authService.session?.Email === 'sean@alegralearning.com';
    }

    onLicenseSaved(savedLicense: PurchasedLicensesInfo): void
    {
        // The org-license-details component handles updating its own data
        // We don't need to do anything here since the license data is managed locally
        console.log('License saved:', savedLicense);
    }
}
